import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import moment from 'moment';
import { Grid } from '@mui/material';
import '../../styles/riskCenter.scss';
import PageHeading from '../../Components/PageHaeding';
import { useLocation, useNavigate } from 'react-router-dom';
import BreadcrumNested from '../../Components/BreadcrumNested';
import { RootState, useAppSelector } from '../../redux/store';
import {
  deleteOrCancelShift,
  getShiftPreviewData,
} from '../../redux/orderSlice';
import ShiftDateHeader from './Components/ShiftDateHeader';
import CustomButton from '../../Components/CustomButton';

import ShiftDetails from './Components/ShiftDetails';
import {
  getEndDayTimestamp,
  getStartDayTimestamp,
} from '../../Utils/helperFns';
import ShiftPreviewShimmer from '../Orders/components/ShiftPreviewShimmer';
import PageLayout from '../../Containers/PageLayout';
import { ROUTES } from '../../helpers/contants';
import WarningDialog from '../../Components/WarningDialog';

export default function PreviewShiftCart() {
  const { product: productDetails } = useLocation()?.state;
  const [pageNo] = useState(1);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedDate, setSelectedDate] = useState<any>(null);
  const [startEndTime, setStartEndTime] = useState<any>({
    startTime: null,
    endTime: null,
  });
  const [checkbox, setCheckBox] = useState<any[]>([]);
  const [warning, setWarning] = useState(false);
  const [isRangeSelected, setIsRangeSelected] = useState(false);
  const { shiftPreviewData, loader } = useAppSelector(
    (state: RootState) => state.order,
  );
  const navigate = useNavigate();

  // console.log('productDetails -=>', productDetails, id);

  // limit=10&cartIds=68973a05354621dc9cb5500e&pageNo=1&toDate=1756578540000&fromDate=1755023400000

  // limit=10&cartIds=689828e5354621dc9cb5669d&pageNo=1&toDate=1755455340000&fromDate=1754937000000

  useEffect(() => {
    const cartId = productDetails?.products
      ?.map((item: any) => item?.cartId)
      ?.join(',');

    // Find the earliest start date from all products
    let earliestStartDate: string | null = null;
    let latestEndDate: string | null = null;
    productDetails?.products?.forEach((item: any) => {
      // Handle shiftFirstDate (start dates)
      if (item.shoppingMode == 'SERVICE') {
        if (item?.startTime) {
          {
            // If it's a single date
            if (
              !earliestStartDate ||
              moment(item.startTime).isBefore(moment(earliestStartDate))
            ) {
              earliestStartDate = item.startTime;
            }
          }
        }
        // Handle shiftLastDate (end dates)
        if (item?.endTime) {
          {
            // If it's a single date
            if (
              !latestEndDate ||
              moment(item.endTime).isAfter(moment(latestEndDate))
            ) {
              latestEndDate = item.endTime;
            }
          }
        }
      }
    });

    // Fallback to startTime/endTime if no shift dates found
    if (!earliestStartDate) {
      earliestStartDate = productDetails?.startTime;
    }
    if (!latestEndDate) {
      latestEndDate = productDetails?.endTime;
    }

    const fromDate = getStartDayTimestamp(
      earliestStartDate || productDetails?.startTime,
    );
    const toDate = getEndDayTimestamp(latestEndDate || productDetails?.endTime);

    const query = `limit=10&cartIds=${cartId}&pageNo=${pageNo}&toDate=${toDate}&fromDate=${fromDate}`;
    // let query = `limit=10&cartIds=689828e5354621dc9cb5669d&pageNo=1&toDate=1755455340000&fromDate=1754937000000`;
    getShiftPreviewData(query);
    setStartEndTime({
      startTime: earliestStartDate,
      endTime: latestEndDate,
    });
  }, [productDetails]);

  const handleDateClick = (date: any) => {
    const selectedFromDate = getStartDayTimestamp(date);
    const selectedToDate = getEndDayTimestamp(date);

    const cartId = productDetails?.products
      ?.map((item: any) => item?.cartId)
      ?.join(',');
    let query = `limit=10&cartIds=${cartId}&pageNo=${pageNo}&toDate=${selectedToDate}&fromDate=${selectedFromDate}`;
    if (selectedDate) {
      if (
        date.isSame(selectedDate?.end, 'day') ||
        date.isSame(selectedDate?.start, 'day') ||
        date.isBetween(selectedDate?.start, selectedDate?.end, 'day')
      ) {
        // clear only if already selected
        const fromDate = getStartDayTimestamp(startEndTime?.startTime);
        const toDate = getEndDayTimestamp(startEndTime?.endTime);
        query = `limit=10&cartIds=${cartId}&pageNo=${pageNo}&toDate=${toDate}&fromDate=${fromDate}`;
        setSelectedDate(null);
        setIsRangeSelected(false);
      } else {
        // rest of your range selection logic...
        const selectedDateEndTimestamp = getEndDayTimestamp(selectedDate?.end);

        if (
          dayjs(date).isAfter(dayjs(selectedDateEndTimestamp)) &&
          !isRangeSelected
        ) {
          const fromDate = getStartDayTimestamp(selectedDate?.start);
          const toDate = getEndDayTimestamp(date);
          query = `limit=10&cartIds=${cartId}&pageNo=${pageNo}&toDate=${toDate}&fromDate=${fromDate}`;
          setSelectedDate({
            start: selectedDate.start,
            end: date,
          });
          setIsRangeSelected(true);
        } else {
          query = `limit=10&cartIds=${cartId}&pageNo=${pageNo}&toDate=${selectedToDate}&fromDate=${selectedFromDate}`;
          setSelectedDate({
            start: date,
            end: date,
          });
          setIsRangeSelected(false);
        }
      }
    } else {
      // no previous selection → select this date
      setSelectedDate({
        start: date,
        end: date,
      });
    }

    getShiftPreviewData(query);
  };

  // Track when data is loaded for the first time
  useEffect(() => {
    if (shiftPreviewData && isInitialLoading) {
      setIsInitialLoading(false);
    }
  }, [shiftPreviewData, isInitialLoading]);

  const totalShifts = useMemo(() => {
    return checkbox.reduce(
      (total: number, item: any) => total + (item?.checkedIds?.length ?? 0),
      0,
    );
  }, [checkbox]);

  return (
    <div className="mainContainer">
      <PageLayout>
        {loader ? (
          <ShiftPreviewShimmer />
        ) : (
          <div className="eventDetail pageBody">
            <PageHeading
              pageTitle={isEditMode ? 'Edit Shift' : 'View Shift'}
              pageName={`Preview Shifts`}
              shiftNavigationUrl={'/cart'}
              buttonChildren={
                !isEditMode ? (
                  <CustomButton
                    text="Edit Shift"
                    showIcon={false}
                    id="add-shift"
                    variant="contained"
                    size="large"
                    color="primary"
                    className="min_width_btn_160"
                    onClick={() => {
                      console.log('Edit Shift');
                      setIsEditMode(true);
                    }}
                  />
                ) : undefined
              }
              isNestedBreadcrum
              breadcrumNested={
                <BreadcrumNested
                  breadcrums={[
                    { name: 'Home', url: '/' },

                    {
                      name: 'Shopping Cart',
                      url: `/cart`,
                    },
                    {
                      name: isEditMode ? 'Edit Shifts' : 'View Shifts',
                      url: '',
                    },
                  ]}
                />
              }
            />

            <div className="">
              <>
                <Grid container spacing={2}>
                  <Grid item md={12} xs={12}>
                    <ShiftDateHeader
                      cartData={{
                        startTime: startEndTime?.startTime,
                        endTime: startEndTime?.endTime,
                        setName: productDetails?.setName,
                      }}
                      handleDateClick={handleDateClick}
                      selectedDate={selectedDate}
                    />

                    <div style={{ marginTop: 24 }}>
                      <ShiftDetails
                        cartData={shiftPreviewData?.data?.data || []}
                        cartDetails={{
                          startTime: startEndTime?.startTime,
                          endTime: startEndTime?.endTime,
                          setName: productDetails?.setName,
                          episodeName: productDetails?.episodeName,
                          episodeNumber: productDetails?.episodeNumber,
                        }}
                        totalWorksheets={shiftPreviewData?.data?.total}
                        isEditMode={isEditMode}
                        setIsEditMode={setIsEditMode}
                        orderDetails={productDetails}
                        orderIds={productDetails?.products
                          ?.map((item: any) => item?.cartId)
                          ?.join(',')}
                        setCheckbox={setCheckBox}
                        setSelectedDate={setSelectedDate}
                        onDeleteShiftsClick={() => setWarning(true)}
                        totalShifts={totalShifts}
                      />
                    </div>
                  </Grid>
                </Grid>
              </>
            </div>
          </div>
        )}
      </PageLayout>
      {/* {console.log('checkbox', checkbox)} */}
      <WarningDialog
        open={warning}
        handleClose={() => setWarning(false)}
        title={`Delete ${totalShifts > 1 ? 'Shifts' : 'Shift'}?`}
        description={`Are you sure you want to delete ${totalShifts} ${
          totalShifts > 1 ? 'shifts' : 'shift'
        }?`}
        onClick={() => {
          deleteOrCancelShift(checkbox, 'cart', () => {
            setWarning(false);
            let remaining = 0;
            let earliestStartDate: string | null = productDetails?.startTime;
            let latestEndDate: string | null = productDetails?.endTime;

            // Iterate shiftPreviewData filtering out deleted shifts
            shiftPreviewData?.data?.data?.forEach((group: any) => {
              group.worksheets.forEach((worksheet: any) => {
                // Filter out shifts included in checkbox by matching shift _id or cartId
                const remainingShifts = worksheet.shifts.filter(
                  (shift: any) =>
                    !checkbox.some(c => c.checkedIds.includes(shift._id)),
                );
                remaining += remainingShifts.length;
                remainingShifts.forEach((shift: any) => {
                  if (shift.actualStartTime) {
                    if (
                      !earliestStartDate ||
                      moment(shift.actualStartTime).isBefore(
                        moment(earliestStartDate),
                      )
                    ) {
                      earliestStartDate = shift.actualStartTime;
                    }
                  }

                  if (shift.actualEndTime) {
                    if (
                      !latestEndDate ||
                      moment(shift.actualEndTime).isAfter(moment(latestEndDate))
                    ) {
                      latestEndDate = shift.actualEndTime;
                    }
                  }
                });
              });
            });
            // Fallback if no dates found
            if (remaining == 0) {
              navigate('/cart', {
                replace: true,
              });
              return;
            }
            const fromDate = getStartDayTimestamp(earliestStartDate);
            const toDate = getEndDayTimestamp(latestEndDate);

            // const cartIds = productDetails?.products
            //   ?.map((item: any) => item?.cartId)
            //   .join(',');

            // getShiftPreviewData(
            //   `limit=10&cartIds=${cartIds}&pageNo=1&toDate=${toDate}&fromDate=${fromDate}`,
            // );
            navigate(`/${ROUTES.shiftDetailCart}`, {
              replace: true,
              state: {
                product: {
                  ...productDetails,
                  products: productDetails.products.map((p: any) =>
                    p.cartId == checkbox[0]?.itemId
                      ? {
                          ...p,
                          // Only add endTime and starttime if the service was extended
                          endTime: toDate,
                          startTime: fromDate,
                        }
                      : p,
                  ),
                },
              },
            });
            setCheckBox([]);
          });
        }}
        cancelBtnText="Cancel"
        confirmBtnText="Yes, Continue"
        type="warning"
      />
    </div>
  );
}
